import { useState, useRef } from "react";
import { toast } from "sonner";
import { Users, ClipboardList, BadgeCheck, Share2, Loader2 } from "lucide-react";
import api from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const STEPS = [
  { icon: ClipboardList, t: "Step 1 — Apply", s: "Fill out the form below — tell us about your audience and how you'd promote our catalog. Every application is reviewed personally." },
  { icon: BadgeCheck, t: "Step 2 — Get Approved", s: "We respond within 3 business days. Approved partners receive a unique referral link and access to branded assets." },
  { icon: Share2, t: "Step 3 — Share and Earn", s: "Share your link on posts, videos, newsletters, or direct outreach. You earn commission on every referred order." },
];

const EMPTY = { name: "", email: "", phone: "", hear_about: "", audience: "", audience_size: "", links: "", promotion_plan: "" };

export default function Affiliate() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/affiliate-applications", form);
      toast.success("Application received — we'll review and reach out within 3 business days.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong submitting your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pb-20" data-testid="affiliate-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16">
        <div className="max-w-3xl animate-fade-up">
          <span className="eyebrow inline-flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Our Affiliate Program</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            Partner with <span style={{ color: "var(--gold)" }}>Ethos Research Labs</span>
          </h1>
          <p className="text-muted-foreground mt-5 max-w-xl leading-relaxed">
            Earn up to 15% commission on every order you refer. Recurring customers, transparent attribution, monthly payouts — built for partners who introduce serious researchers to a brand they'll trust.
          </p>
          <button onClick={scrollToForm} className="mt-7 h-12 px-7 rounded-xl btn-ink font-display font-bold" data-testid="affiliate-hero-cta">
            Become an Affiliate
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12">
          {[["Up to 15%", "Commission"], ["Monthly", "Payouts via PayPal"], ["Recurring", "On every reorder"]].map(([a, b], i) => (
            <div key={i} className="card-soft rounded-2xl p-6 text-center">
              <p className="font-display font-extrabold text-3xl" style={{ color: "var(--gold)" }}>{a}</p>
              <p className="text-sm text-muted-foreground mt-1">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-center">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {STEPS.map(({ icon: Icon, t, s }, i) => (
            <div key={i} className="card-soft rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-lg">{t}</h3>
                <Icon className="w-5 h-5" style={{ color: "var(--gold)" }} />
              </div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section ref={formRef} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 scroll-mt-24">
        <div className="text-center">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl">Researchers, innovators, and industry professionals</h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
            We work with independent researchers, laboratory professionals, and trusted voices in the scientific community whose audiences value quality and transparency. Approved partners receive a dedicated point of contact and early access to upcoming product launches.
          </p>
        </div>

        <form onSubmit={submit} className="card-soft rounded-2xl p-6 sm:p-8 mt-10 space-y-5" data-testid="affiliate-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-bold mb-2" htmlFor="aff-name">Name <span className="text-destructive">*</span></label>
              <Input id="aff-name" value={form.name} onChange={set("name")} required maxLength={200} className="h-11 bg-white border-border" data-testid="affiliate-name" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2" htmlFor="aff-email">Email <span className="text-destructive">*</span></label>
              <Input id="aff-email" type="email" value={form.email} onChange={set("email")} required maxLength={254} className="h-11 bg-white border-border" data-testid="affiliate-email" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" htmlFor="aff-phone">Phone</label>
            <Input id="aff-phone" value={form.phone} onChange={set("phone")} maxLength={50} className="h-11 bg-white border-border" data-testid="affiliate-phone" />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" htmlFor="aff-hear">How did you hear about us?</label>
            <Input id="aff-hear" value={form.hear_about} onChange={set("hear_about")} maxLength={500} placeholder="A friend, podcast, social post, search" className="h-11 bg-white border-border" data-testid="affiliate-hear" />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" htmlFor="aff-audience">Tell us about your audience</label>
            <Textarea id="aff-audience" value={form.audience} onChange={set("audience")} maxLength={3000} placeholder="Who are they, where do you reach them, and what do they care about?" className="min-h-32 bg-white border-border" data-testid="affiliate-audience" />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" htmlFor="aff-size">Audience Size</label>
            <Input id="aff-size" value={form.audience_size} onChange={set("audience_size")} maxLength={200} placeholder="Optional. Rough total across all channels." className="h-11 bg-white border-border" data-testid="affiliate-size" />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" htmlFor="aff-links">Links</label>
            <Textarea id="aff-links" value={form.links} onChange={set("links")} maxLength={2000} placeholder={"https://example.com\nhttps://instagram.com/yourhandle"} className="min-h-24 bg-white border-border" data-testid="affiliate-links" />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" htmlFor="aff-plan">How do you plan to promote Ethos Research Labs?</label>
            <Textarea id="aff-plan" value={form.promotion_plan} onChange={set("promotion_plan")} maxLength={3000} placeholder="Content strategy, channels, angle - anything that helps us picture the fit." className="min-h-32 bg-white border-border" data-testid="affiliate-plan" />
          </div>

          <button type="submit" disabled={submitting} className="h-12 px-8 rounded-xl btn-ink font-display font-bold inline-flex items-center gap-2 disabled:opacity-60" data-testid="affiliate-submit">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Sending…" : "Send"}
          </button>
        </form>
      </section>
    </div>
  );
}
